import React, { createContext, useMemo, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { UI_SLOTS, isRenderBindingMarker } from '@almadar/core';
import { createLogger } from '@almadar/logger';
import { jsx } from 'react/jsx-runtime';
export { ANONYMOUS_USER, CurrentPagePathContext, CurrentPagePathProvider, OrbitalThemeProvider, UserContext, UserProvider, useCurrentPagePath, useHasPermission, useHasRole, useUser, useUserForEvaluation } from '@almadar/ui/providers';
import { ThemeProvider as ThemeProvider$1, useTheme as useTheme$1 } from '@almadar/ui/context';

function expressionEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!expressionEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (a !== null && b !== null && typeof a === "object" && typeof b === "object" && !Array.isArray(a) && !Array.isArray(b) && !(a instanceof Date) && !(b instanceof Date)) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!expressionEqual(
        a[key],
        b[key]
      )) return false;
    }
    return true;
  }
  return false;
}
function isPlainSlotObject(value) {
  if (value === null || value === void 0 || typeof value !== "object") return false;
  if (Array.isArray(value)) return false;
  if (React.isValidElement(value)) return false;
  if (value instanceof Date) return false;
  if (isRenderBindingMarker(value)) return false;
  return true;
}
function shareValue(prev, next) {
  if (Object.is(prev, next)) return { value: prev, equal: true };
  const prevMarker = isRenderBindingMarker(prev) ? prev : void 0;
  const nextMarker = isRenderBindingMarker(next) ? next : void 0;
  if (prevMarker !== void 0 || nextMarker !== void 0) {
    if (prevMarker !== void 0 && nextMarker !== void 0 && expressionEqual(prevMarker.expression, nextMarker.expression)) {
      return { value: prev, equal: true };
    }
    return { value: next, equal: false };
  }
  if (prev instanceof Date || next instanceof Date) {
    return prev instanceof Date && next instanceof Date && prev.getTime() === next.getTime() ? { value: prev, equal: true } : { value: next, equal: false };
  }
  if (typeof prev === "function" || typeof next === "function") return { value: next, equal: false };
  if (React.isValidElement(prev) || React.isValidElement(next)) return { value: next, equal: false };
  if (Array.isArray(prev) && Array.isArray(next)) {
    let equal = prev.length === next.length;
    const out = new Array(next.length);
    for (let i = 0; i < next.length; i++) {
      const child = shareValue(prev[i], next[i]);
      out[i] = child.value;
      if (!child.equal) equal = false;
    }
    return equal ? { value: prev, equal: true } : { value: out, equal: false };
  }
  if (isPlainSlotObject(prev) && isPlainSlotObject(next)) {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    let equal = prevKeys.length === nextKeys.length;
    const out = {};
    for (const key of nextKeys) {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) {
        equal = false;
        out[key] = next[key];
        continue;
      }
      const child = shareValue(prev[key], next[key]);
      out[key] = child.value;
      if (!child.equal) equal = false;
    }
    return equal ? { value: prev, equal: true } : { value: out, equal: false };
  }
  return { value: next, equal: false };
}
function reconcileSlotProps(prev, next) {
  const result = shareValue(prev, next);
  return { value: result.value, equal: result.equal };
}

// hooks/useUISlots.ts
var log = createLogger("almadar:ui:ui-slots");
var DEFAULT_SOURCE_KEY = "__default__";
var MULTI_SOURCE_STACK_TRAIT = "__multi_source_stack__";
var ALL_SLOTS = UI_SLOTS.filter(
  (slot) => !slot.includes(".") && slot !== "hud" && slot !== "screen"
);
var DEFAULT_SLOTS = ALL_SLOTS.reduce(
  (acc, slot) => {
    acc[slot] = null;
    return acc;
  },
  {}
);
var DEFAULT_SOURCES = ALL_SLOTS.reduce(
  (acc, slot) => {
    acc[slot] = {};
    return acc;
  },
  {}
);
function aggregateSlot(sources) {
  if (!sources) return null;
  const entries = Object.entries(sources);
  if (entries.length === 0) return null;
  if (entries.length === 1) return entries[0][1];
  const children = entries.map(([sourceKey, entry]) => ({
    type: entry.pattern,
    ...entry.props,
    ...sourceKey !== DEFAULT_SOURCE_KEY && { _sourceTrait: sourceKey }
  }));
  const stackId = `slot-content-stack-${entries.map(([k]) => k).join("-")}`;
  return {
    id: stackId,
    pattern: "stack",
    props: {
      direction: "vertical",
      gap: "lg",
      children
    },
    priority: 0,
    animation: "fade",
    sourceTrait: MULTI_SOURCE_STACK_TRAIT
  };
}
function useUISlotManager() {
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const subscribersRef = useRef(/* @__PURE__ */ new Set());
  const timersRef = useRef(/* @__PURE__ */ new Map());
  const traitIndexRef = useRef(/* @__PURE__ */ new Map());
  const traitSubscribersRef = useRef(/* @__PURE__ */ new Map());
  const slots = useMemo(() => {
    const out = { ...DEFAULT_SLOTS };
    for (const slot of ALL_SLOTS) {
      out[slot] = aggregateSlot(sources[slot]);
    }
    return out;
  }, [sources]);
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);
  const notifySubscribers = useCallback((slot, content) => {
    subscribersRef.current.forEach((callback) => {
      try {
        callback(slot, content);
      } catch (error) {
        log.error("Subscriber error", { error: error instanceof Error ? error : String(error) });
      }
    });
  }, []);
  const notifyTraitSubscribers = useCallback(
    (traitName, content) => {
      const subs = traitSubscribersRef.current.get(traitName);
      if (!subs) return;
      subs.forEach((callback) => {
        try {
          callback(content);
        } catch (error) {
          log.error("Trait subscriber error", { traitName, error: error instanceof Error ? error : String(error) });
        }
      });
    },
    []
  );
  const indexTraitRender = useCallback(
    (traitName, content) => {
      traitIndexRef.current.set(traitName, content);
    },
    []
  );
  const unindexTrait = useCallback((traitName) => {
    traitIndexRef.current.delete(traitName);
  }, []);
  const render = useCallback(
    (config) => {
      const sourceKey = config.sourceTrait ?? DEFAULT_SOURCE_KEY;
      const id = `slot-content-${config.target}-${sourceKey}`;
      const content = {
        id,
        pattern: config.pattern,
        props: config.props ?? {},
        priority: config.priority ?? 0,
        animation: config.animation ?? "fade",
        onDismiss: config.onDismiss,
        sourceTrait: config.sourceTrait,
        slot: config.target,
        transitionEvent: config.transitionEvent,
        fromState: config.fromState,
        entity: config.entity
      };
      if (config.autoDismissMs && config.autoDismissMs > 0) {
        content.autoDismissAt = Date.now() + config.autoDismissMs;
        const prevTimer = timersRef.current.get(id);
        if (prevTimer) clearTimeout(prevTimer);
        const timer = setTimeout(() => {
          setSources((prev) => {
            const slotSources = prev[config.target];
            if (slotSources && slotSources[sourceKey] === content) {
              content.onDismiss?.();
              const next = { ...slotSources };
              delete next[sourceKey];
              const updated = { ...prev, [config.target]: next };
              notifySubscribers(config.target, aggregateSlot(next));
              return updated;
            }
            return prev;
          });
          timersRef.current.delete(id);
        }, config.autoDismissMs);
        timersRef.current.set(id, timer);
      }
      setSources((prev) => {
        const slotSources = prev[config.target] ?? {};
        const existing = slotSources[sourceKey];
        if (existing && existing.priority > content.priority) {
          log.warn("Slot already has higher priority content", {
            slot: config.target,
            sourceKey,
            existingPriority: existing.priority,
            newPriority: content.priority
          });
          return prev;
        }
        if (existing && existing.priority === content.priority && existing.pattern === content.pattern && existing.animation === content.animation && existing.transitionEvent === content.transitionEvent && existing.fromState === content.fromState && existing.entity === content.entity && existing.nodeId === content.nodeId && existing.autoDismissAt === void 0 && content.autoDismissAt === void 0 && existing.onDismiss === void 0 === (content.onDismiss === void 0)) {
          const reconciled = reconcileSlotProps(existing.props, content.props);
          if (reconciled.equal) {
            log.debug("slot:flush-bail", { slot: config.target, sourceKey, pattern: content.pattern });
            return prev;
          }
          content.props = reconciled.value;
        }
        const nextSources = {
          ...slotSources,
          [sourceKey]: content
        };
        const nextAll = { ...prev, [config.target]: nextSources };
        const priorWriters = Object.keys(slotSources);
        if (priorWriters.length === 1 && priorWriters[0] !== sourceKey) {
          log.warn("slot:contention", {
            slot: config.target,
            writers: [priorWriters[0], sourceKey],
            patternTypes: [slotSources[priorWriters[0]].pattern, content.pattern]
          });
        }
        if (content.sourceTrait) {
          indexTraitRender(content.sourceTrait, content);
          notifyTraitSubscribers(content.sourceTrait, content);
        }
        log.info("slot:written", {
          slot: config.target,
          sourceKey,
          sourceTrait: content.sourceTrait,
          patternType: content.pattern,
          priority: content.priority
        });
        notifySubscribers(config.target, aggregateSlot(nextSources));
        return nextAll;
      });
      return id;
    },
    [notifySubscribers, notifyTraitSubscribers, indexTraitRender]
  );
  const clear = useCallback(
    (slot) => {
      setSources((prev) => {
        const slotSources = prev[slot];
        if (!slotSources || Object.keys(slotSources).length === 0) {
          return prev;
        }
        for (const content of Object.values(slotSources)) {
          const timer = timersRef.current.get(content.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(content.id);
          }
          content.onDismiss?.();
          if (content.sourceTrait) {
            unindexTrait(content.sourceTrait);
            notifyTraitSubscribers(content.sourceTrait, null);
          }
        }
        notifySubscribers(slot, null);
        return { ...prev, [slot]: {} };
      });
    },
    [notifySubscribers, notifyTraitSubscribers, unindexTrait]
  );
  const clearBySource = useCallback(
    (slot, sourceTrait) => {
      const sourceKey = sourceTrait;
      setSources((prev) => {
        const slotSources = prev[slot];
        if (!slotSources || !(sourceKey in slotSources)) {
          log.debug("slot:clear-noop", { slot, sourceTrait, reason: !slotSources ? "no-slot" : "no-source" });
          return prev;
        }
        const content = slotSources[sourceKey];
        const timer = timersRef.current.get(content.id);
        if (timer) {
          clearTimeout(timer);
          timersRef.current.delete(content.id);
        }
        content.onDismiss?.();
        if (content.sourceTrait) {
          unindexTrait(content.sourceTrait);
          notifyTraitSubscribers(content.sourceTrait, null);
        }
        const nextSources = { ...slotSources };
        delete nextSources[sourceKey];
        log.info("slot:cleared", { slot, sourceTrait, lastPatternType: content.pattern });
        notifySubscribers(slot, aggregateSlot(nextSources));
        return { ...prev, [slot]: nextSources };
      });
    },
    [notifySubscribers, notifyTraitSubscribers, unindexTrait]
  );
  const clearById = useCallback(
    (id) => {
      setSources((prev) => {
        for (const slot of ALL_SLOTS) {
          const slotSources = prev[slot];
          if (!slotSources) continue;
          const matchKey = Object.keys(slotSources).find(
            (k) => slotSources[k].id === id
          );
          if (!matchKey) continue;
          const content = slotSources[matchKey];
          const timer = timersRef.current.get(id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
          }
          content.onDismiss?.();
          if (content.sourceTrait) {
            unindexTrait(content.sourceTrait);
            notifyTraitSubscribers(content.sourceTrait, null);
          }
          const nextSources = { ...slotSources };
          delete nextSources[matchKey];
          notifySubscribers(slot, aggregateSlot(nextSources));
          return { ...prev, [slot]: nextSources };
        }
        return prev;
      });
    },
    [notifySubscribers, notifyTraitSubscribers, unindexTrait]
  );
  const clearAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setSources((prev) => {
      for (const slot of ALL_SLOTS) {
        const slotSources = prev[slot];
        if (!slotSources) continue;
        for (const content of Object.values(slotSources)) {
          content.onDismiss?.();
          if (content.sourceTrait) {
            notifyTraitSubscribers(content.sourceTrait, null);
          }
        }
        notifySubscribers(slot, null);
      }
      return DEFAULT_SOURCES;
    });
    traitIndexRef.current.clear();
  }, [notifySubscribers, notifyTraitSubscribers]);
  const subscribe = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);
  const hasContent = useCallback(
    (slot) => slots[slot] !== null,
    [slots]
  );
  const getContent = useCallback(
    (slot) => slots[slot] ?? null,
    [slots]
  );
  const getTraitContent = useCallback(
    (traitName) => traitIndexRef.current.get(traitName) ?? null,
    []
  );
  const subscribeTrait = useCallback(
    (traitName, callback) => {
      let set = traitSubscribersRef.current.get(traitName);
      if (!set) {
        set = /* @__PURE__ */ new Set();
        traitSubscribersRef.current.set(traitName, set);
      }
      set.add(callback);
      return () => {
        const s = traitSubscribersRef.current.get(traitName);
        if (!s) return;
        s.delete(callback);
        if (s.size === 0) {
          traitSubscribersRef.current.delete(traitName);
        }
      };
    },
    []
  );
  const updateTraitContent = useCallback(
    (traitName, content) => {
      const id = `slot-content-trait-${traitName}`;
      const fullContent = { ...content, id, sourceTrait: traitName };
      indexTraitRender(traitName, fullContent);
      notifyTraitSubscribers(traitName, fullContent);
      return id;
    },
    [indexTraitRender, notifyTraitSubscribers]
  );
  return {
    slots,
    render,
    clear,
    clearBySource,
    clearById,
    clearAll,
    subscribe,
    hasContent,
    getContent,
    getTraitContent,
    subscribeTrait,
    updateTraitContent
  };
}
var UISlotContext = createContext(null);
function UISlotProvider({ children }) {
  const slotManager = useUISlotManager();
  const contextValue = useMemo(() => slotManager, [slotManager]);
  return /* @__PURE__ */ jsx(UISlotContext.Provider, { value: contextValue, children });
}
function useUISlots() {
  const context = useContext(UISlotContext);
  if (!context) {
    throw new Error(
      "useUISlots must be used within a UISlotProvider. Make sure your component tree is wrapped with <UISlotProvider>."
    );
  }
  return context;
}
function useSlotContent(slot) {
  const { getContent } = useUISlots();
  return getContent(slot);
}
function useSlotHasContent(slot) {
  const { hasContent } = useUISlots();
  return hasContent(slot);
}
var log2 = createLogger("almadar:ui:theme");
var BUILT_IN_THEMES = [
  {
    name: "wireframe",
    displayName: "Wireframe",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "minimalist",
    displayName: "Minimalist",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "almadar",
    displayName: "Almadar",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "trait-wars",
    displayName: "Trait Wars",
    hasLightMode: false,
    hasDarkMode: true
  },
  {
    name: "game-sci-fi",
    displayName: "Game: Sci-Fi",
    hasLightMode: false,
    hasDarkMode: true
  },
  {
    name: "game-ui-pack",
    displayName: "Game: Flat UI",
    hasLightMode: false,
    hasDarkMode: true
  },
  {
    name: "game-adventure",
    displayName: "Game: Adventure",
    hasLightMode: false,
    hasDarkMode: true
  },
  {
    name: "game-rpg",
    displayName: "Game: RPG",
    hasLightMode: false,
    hasDarkMode: true
  },
  // Extended themes
  {
    name: "neon",
    displayName: "Neon",
    hasLightMode: true,
    hasDarkMode: true
  },
  // Layer 1 skin axes — truly-unique themes (compact tech / editorial / brutalist dense / display-heavy / touch-first)
  {
    name: "prism",
    displayName: "Prism",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "gazette",
    displayName: "Gazette",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "terminal",
    displayName: "Terminal",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "atelier",
    displayName: "Atelier",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "kiosk",
    displayName: "Kiosk",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "linear-clean",
    displayName: "Linear Clean",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "notion-editorial",
    displayName: "Notion Editorial",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "bloomberg-dense",
    displayName: "Bloomberg Dense",
    hasLightMode: true,
    hasDarkMode: true
  },
  // Personality themes — high-demand web styles (glassmorphism / claymorphism / retro / enterprise)
  {
    name: "glass",
    displayName: "Glass",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "clay",
    displayName: "Clay",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "retro",
    displayName: "Retro",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "corporate",
    displayName: "Corporate",
    hasLightMode: true,
    hasDarkMode: true
  },
  {
    name: "comic",
    displayName: "Comic Studio",
    hasLightMode: true,
    hasDarkMode: true
  }
];
var ThemeContext = createContext(void 0);
var THEME_STORAGE_KEY = "theme";
var MODE_STORAGE_KEY = "theme-mode";
function getSystemMode() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolveMode(mode) {
  if (mode === "system") {
    return getSystemMode();
  }
  return mode;
}
var ThemeProvider = ({
  children,
  themes = [],
  defaultTheme = "wireframe",
  defaultMode = "system",
  targetRef
}) => {
  const availableThemes = useMemo(() => {
    const themeMap = /* @__PURE__ */ new Map();
    BUILT_IN_THEMES.forEach((t) => themeMap.set(t.name, t));
    themes.forEach((t) => themeMap.set(t.name, t));
    return Array.from(themeMap.values());
  }, [themes]);
  const isScoped = !!targetRef;
  const [theme, setThemeState] = useState(() => {
    if (isScoped || typeof window === "undefined") return defaultTheme;
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const validThemes = [
      ...BUILT_IN_THEMES.map((t) => t.name),
      ...themes.map((t) => t.name)
    ];
    if (stored && validThemes.includes(stored)) {
      return stored;
    }
    return defaultTheme;
  });
  const [mode, setModeState] = useState(() => {
    if (isScoped || typeof window === "undefined") return defaultMode;
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
    return defaultMode;
  });
  const [resolvedMode, setResolvedMode] = useState(
    () => resolveMode(mode)
  );
  const appliedTheme = useMemo(
    () => `${theme}-${resolvedMode}`,
    [theme, resolvedMode]
  );
  useEffect(() => {
    const updateResolved = () => {
      setResolvedMode(resolveMode(mode));
    };
    updateResolved();
    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => updateResolved();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    return void 0;
  }, [mode]);
  useEffect(() => {
    if (isScoped) {
      if (targetRef?.current) {
        targetRef.current.setAttribute("data-theme", appliedTheme);
        targetRef.current.classList.remove("light", "dark");
        targetRef.current.classList.add(resolvedMode);
      }
      return;
    }
    const root = document.documentElement;
    root.setAttribute("data-theme", appliedTheme);
    root.style.colorScheme = resolvedMode;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedMode);
  }, [appliedTheme, resolvedMode, targetRef, isScoped]);
  const setTheme = useCallback(
    (newTheme) => {
      const validTheme = availableThemes.find((t) => t.name === newTheme);
      if (validTheme) {
        setThemeState(newTheme);
        if (!isScoped && typeof window !== "undefined") {
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        }
      } else {
        log2.warn("Theme not found", {
          theme: newTheme,
          available: availableThemes.map((t) => t.name)
        });
      }
    },
    [availableThemes]
  );
  const setMode = useCallback((newMode) => {
    setModeState(newMode);
    if (!isScoped && typeof window !== "undefined") {
      localStorage.setItem(MODE_STORAGE_KEY, newMode);
    }
  }, []);
  const toggleMode = useCallback(() => {
    const newMode = resolvedMode === "dark" ? "light" : "dark";
    setMode(newMode);
  }, [resolvedMode, setMode]);
  const contextValue = useMemo(
    () => ({
      theme,
      mode,
      resolvedMode,
      setTheme,
      setMode,
      toggleMode,
      availableThemes,
      appliedTheme
    }),
    [
      theme,
      mode,
      resolvedMode,
      setTheme,
      setMode,
      toggleMode,
      availableThemes,
      appliedTheme
    ]
  );
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: contextValue, children });
};
function useTheme() {
  const context = useContext(ThemeContext);
  if (context === void 0) {
    return {
      theme: "wireframe",
      mode: "light",
      resolvedMode: "light",
      setTheme: () => {
      },
      setMode: () => {
      },
      toggleMode: () => {
      },
      availableThemes: BUILT_IN_THEMES,
      appliedTheme: "wireframe-light"
    };
  }
  return context;
}
var ThemeContext_default = ThemeContext;

// lib/themeTokens.ts
function applyFlat(obj, table, vars) {
  if (!obj) return;
  for (const { cssVar, key } of table) {
    const value = obj[key];
    if (value !== void 0) vars[cssVar] = value;
  }
}
var DENSITY_SPACING_VARS = [
  { cssVar: "--space-0", key: "space0" },
  { cssVar: "--space-1", key: "space1" },
  { cssVar: "--space-2", key: "space2" },
  { cssVar: "--space-3", key: "space3" },
  { cssVar: "--space-4", key: "space4" },
  { cssVar: "--space-5", key: "space5" },
  { cssVar: "--space-6", key: "space6" },
  { cssVar: "--space-7", key: "space7" },
  { cssVar: "--space-8", key: "space8" },
  { cssVar: "--space-9", key: "space9" },
  { cssVar: "--space-10", key: "space10" },
  { cssVar: "--space-11", key: "space11" },
  { cssVar: "--space-12", key: "space12" }
];
var DENSITY_ELEMENT_VARS = [
  { cssVar: "--button-height-sm", key: "buttonHeightSm" },
  { cssVar: "--button-height-md", key: "buttonHeightMd" },
  { cssVar: "--button-height-lg", key: "buttonHeightLg" },
  { cssVar: "--input-height-sm", key: "inputHeightSm" },
  { cssVar: "--input-height-md", key: "inputHeightMd" },
  { cssVar: "--input-height-lg", key: "inputHeightLg" },
  { cssVar: "--row-height-compact", key: "rowHeightCompact" },
  { cssVar: "--row-height-normal", key: "rowHeightNormal" },
  { cssVar: "--row-height-spacious", key: "rowHeightSpacious" },
  { cssVar: "--card-padding-sm", key: "cardPaddingSm" },
  { cssVar: "--card-padding-md", key: "cardPaddingMd" },
  { cssVar: "--card-padding-lg", key: "cardPaddingLg" },
  { cssVar: "--dialog-padding", key: "dialogPadding" },
  { cssVar: "--section-gap", key: "sectionGap" }
];
var TYPE_FAMILY_VARS = [
  { cssVar: "--font-family-display", key: "displayFamily" },
  { cssVar: "--font-family-body", key: "bodyFamily" },
  { cssVar: "--font-family-mono", key: "monoFamily" }
];
var TYPE_SIZE_KEYS = [
  "xs",
  "sm",
  "base",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "display-1",
  "display-2"
];
var TYPE_INTENT_VARS = [
  { key: "headingMajor", cssName: "heading-major" },
  { key: "headingMinor", cssName: "heading-minor" },
  { key: "bodyEmphasis", cssName: "body-emphasis" },
  { key: "bodyDefault", cssName: "body-default" },
  { key: "bodyQuiet", cssName: "body-quiet" },
  { key: "caption", cssName: "caption" },
  { key: "numeric", cssName: "numeric" }
];
var MOTION_DURATION_KEYS = [
  "instant",
  "fast",
  "normal",
  "slow",
  "dramatic"
];
var MOTION_EASING_KEYS = [
  "linear",
  "standard",
  "emphasized",
  "spring"
];
var MOTION_INTENT_VARS = [
  { key: "enter", cssName: "enter" },
  { key: "exit", cssName: "exit" },
  { key: "hover", cssName: "hover" },
  { key: "press", cssName: "press" },
  { key: "expand", cssName: "expand" },
  { key: "transition", cssName: "transition" }
];
var ICONOGRAPHY_VARS = [
  { cssVar: "--icon-family", key: "family" },
  { cssVar: "--icon-stroke-width", key: "strokeWidth" },
  { cssVar: "--icon-default-size", key: "defaultSize" }
];
var ELEVATION_VARS = [
  { cssVar: "--elevation-card", key: "cardElevation" },
  { cssVar: "--elevation-popover", key: "popoverElevation" },
  { cssVar: "--elevation-dialog", key: "dialogElevation" },
  { cssVar: "--elevation-toast", key: "toastElevation" }
];
var GEOMETRY_VARS = [
  { cssVar: "--radius-container", key: "radiusContainer" },
  { cssVar: "--radius-interactive", key: "radiusInteractive" },
  { cssVar: "--radius-pill", key: "radiusPill" },
  { cssVar: "--border-hairline", key: "borderHairline" },
  { cssVar: "--border-standard", key: "borderStandard" },
  { cssVar: "--border-heavy", key: "borderHeavy" }
];
var LEGACY_PREFIXES = {
  colors: "--color-",
  radii: "--radius-",
  spacing: "--space-",
  shadows: "--shadow-"
};
function themeTokensToCssVars(tokens, mode = "light", darkVariant) {
  const vars = {};
  const isDark = mode === "dark";
  const pickColors = isDark && darkVariant?.colors ? darkVariant.colors : tokens.colors;
  if (pickColors) {
    for (const [key, value] of Object.entries(pickColors)) {
      vars[`${LEGACY_PREFIXES.colors}${key}`] = value;
    }
    if (isDark && darkVariant?.colors && tokens.colors) {
      for (const [key, value] of Object.entries(tokens.colors)) {
        const varName = `${LEGACY_PREFIXES.colors}${key}`;
        if (!(varName in vars)) vars[varName] = value;
      }
    }
  }
  const pickRadii = isDark && darkVariant?.radii ? darkVariant.radii : tokens.radii;
  if (pickRadii) {
    for (const [key, value] of Object.entries(pickRadii)) {
      vars[`${LEGACY_PREFIXES.radii}${key}`] = value;
    }
  }
  const pickSpacing = isDark && darkVariant?.spacing ? darkVariant.spacing : tokens.spacing;
  if (pickSpacing) {
    for (const [key, value] of Object.entries(pickSpacing)) {
      vars[`${LEGACY_PREFIXES.spacing}${key}`] = value;
    }
  }
  const pickTypography = isDark && darkVariant?.typography ? darkVariant.typography : tokens.typography;
  if (pickTypography) {
    for (const [key, value] of Object.entries(pickTypography)) {
      vars[`--${key}`] = value;
    }
  }
  const pickShadows = isDark && darkVariant?.shadows ? darkVariant.shadows : tokens.shadows;
  if (pickShadows) {
    for (const [key, value] of Object.entries(pickShadows)) {
      vars[`${LEGACY_PREFIXES.shadows}${key}`] = value;
    }
  }
  const pickDensity = isDark && darkVariant?.density ? darkVariant.density : tokens.density;
  emitDensity(pickDensity, vars);
  const pickTypeScale = isDark && darkVariant?.typeScale ? darkVariant.typeScale : tokens.typeScale;
  emitTypeScale(pickTypeScale, vars);
  const pickMotion = isDark && darkVariant?.motion ? darkVariant.motion : tokens.motion;
  emitMotion(pickMotion, vars);
  const pickIconography = isDark && darkVariant?.iconography ? darkVariant.iconography : tokens.iconography;
  emitIconography(pickIconography, vars);
  const pickElevation = isDark && darkVariant?.elevation ? darkVariant.elevation : tokens.elevation;
  emitElevation(pickElevation, vars);
  const pickGeometry = isDark && darkVariant?.geometry ? darkVariant.geometry : tokens.geometry;
  emitGeometry(pickGeometry, vars);
  return vars;
}
function emitDensity(density, vars) {
  if (!density) return;
  applyFlat(density.spacing, DENSITY_SPACING_VARS, vars);
  applyFlat(density, DENSITY_ELEMENT_VARS, vars);
}
function typeSizeKeyStr(k) {
  return k;
}
function typeWeightStr(w) {
  return w;
}
function emitTypeIntent(name, intent, vars) {
  const sizeKey = typeSizeKeyStr(intent.size);
  const weight = typeWeightStr(intent.weight);
  vars[`--intent-${name}-size`] = `var(--text-${sizeKey})`;
  vars[`--intent-${name}-weight`] = `var(--font-weight-${weight})`;
  vars[`--intent-${name}-leading`] = `var(--leading-${sizeKey})`;
}
function emitTypeScale(ts, vars) {
  if (!ts) return;
  applyFlat(ts, TYPE_FAMILY_VARS, vars);
  if (ts.scale) {
    const s = ts.scale;
    for (const k of TYPE_SIZE_KEYS) {
      const entry = s[k];
      if (entry !== void 0) {
        vars[`--text-${k}`] = entry.size;
        vars[`--leading-${k}`] = entry.lineHeight;
      }
    }
  }
  if (ts.intents) {
    const i = ts.intents;
    for (const { key, cssName } of TYPE_INTENT_VARS) {
      const intent = i[key];
      if (intent) emitTypeIntent(cssName, intent, vars);
    }
  }
}
function emitMotionIntent(name, intent, vars) {
  vars[`--intent-${name}-duration`] = `var(--duration-${intent.duration})`;
  vars[`--intent-${name}-easing`] = `var(--easing-${intent.easing})`;
}
function emitMotion(m, vars) {
  if (!m) return;
  if (m.durations) {
    const d = m.durations;
    for (const k of MOTION_DURATION_KEYS) {
      const v = d[k];
      if (v !== void 0) vars[`--duration-${k}`] = v;
    }
  }
  if (m.easings) {
    const e = m.easings;
    for (const k of MOTION_EASING_KEYS) {
      const v = e[k];
      if (v !== void 0) vars[`--easing-${k}`] = v;
    }
  }
  if (m.intents) {
    const i = m.intents;
    for (const { key, cssName } of MOTION_INTENT_VARS) {
      const intent = i[key];
      if (intent) emitMotionIntent(cssName, intent, vars);
    }
  }
}
function emitIconography(i, vars) {
  applyFlat(i, ICONOGRAPHY_VARS, vars);
}
function emitElevation(e, vars) {
  applyFlat(e, ELEVATION_VARS, vars);
}
function emitGeometry(g, vars) {
  applyFlat(g, GEOMETRY_VARS, vars);
}
function resolveThemeForRuntime(theme) {
  if (theme === void 0) return void 0;
  if (typeof theme === "string") return void 0;
  return theme;
}
var DesignThemeProvider = ThemeProvider$1;
function useDesignTheme() {
  const { theme, setTheme, availableThemes } = useTheme$1();
  return {
    designTheme: theme,
    setDesignTheme: setTheme,
    availableThemes: availableThemes.map((t) => t.name)
  };
}

export { BUILT_IN_THEMES, DesignThemeProvider, ThemeContext_default as ThemeContext, ThemeProvider, UISlotContext, UISlotProvider, resolveThemeForRuntime, themeTokensToCssVars, useDesignTheme, useSlotContent, useSlotHasContent, useTheme, useUISlots };
